import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Activity, ShieldCheck, Users, HeartPulse, Sparkles, Building2 } from 'lucide-react';
import PhoneMockup from '@/components/PhoneMockup';
import Pipeline from '@/components/Pipeline';
import SectionHeader from '@/components/SectionHeader';
import PageMeta from '@/components/PageMeta';
import StructuredFeedbackSection from '@/components/StructuredFeedbackSection';
import { HERO } from '@/constants/testIds';

const FEEDBACK_ITEMS = [
  { icon: Camera, label: 'Movement setup', body: 'Camera position, framing and what is visible in the frame.' },
  { icon: Activity, label: 'Visible positioning', body: 'How the body appears to be aligned for the selected movement.' },
  { icon: ArrowRight, label: 'Range of motion', body: 'Where supported, an indication of how the movement progresses.' },
  { icon: Sparkles, label: 'Repetition phases', body: 'How a movement is broken into recognisable phases.' },
  { icon: Activity, label: 'Tempo', body: 'Visible changes in the speed of a movement across repetitions.' },
  { icon: Sparkles, label: 'Movement consistency', body: 'How steady the movement looks across the recorded set.' },
  { icon: ShieldCheck, label: 'Trunk control', body: 'Visible posture and stability through the torso, where appropriate.' },
  { icon: Sparkles, label: 'What appeared to go well', body: 'A reminder of the parts of the movement that looked confident.' },
  { icon: HeartPulse, label: 'Areas to revisit', body: 'Plain-language notes about what may deserve more attention.' },
  { icon: ShieldCheck, label: 'When the system is uncertain', body: 'A clear signal when the available video evidence is not strong enough.' },
];

const AUDIENCE_CARDS = [
  {
    icon: HeartPulse,
    title: 'Recovery and rehabilitation',
    body: 'Bridging the space between professional appointments and what happens at home, between sessions.',
    image: 'https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwaHlzaWNhbCUyMHRoZXJhcHklMjBjYWxtfGVufDB8fHx8MTc4MTYwMTYzNXww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Users,
    title: 'People facing adversity',
    body: 'Illness, injury, surgery, ageing, pain, fatigue or long interruptions to ordinary movement.',
    image: 'https://images.unsplash.com/photo-1764628039015-222c6d30c68d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxzZW5pb3IlMjB3YWxraW5nJTIwbmF0dXJlfGVufDB8fHx8MTc4MTYwMTYyOHww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Activity,
    title: 'Everyday movement and strength',
    body: 'Clearer awareness of how everyday movements actually look — beyond what a mirror can show.',
    image: 'https://images.unsplash.com/photo-1552650272-b8a34e21bc4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaG9tZSUyMHN0cmV0Y2hpbmd8ZW58MHx8fHwxNzgxNjAxNjI4fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Building2,
    title: 'Future professional applications',
    body: 'Movement intelligence for allied health, rehabilitation, fitness and platform partners. Currently in research.',
    image: 'https://images.unsplash.com/photo-1592890288564-76628a30a657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwc21hcnRwaG9uZSUyMGhhbmRzfGVufDB8fHx8MTc4MTYwMTYyOHww&ixlib=rb-4.1.0&q=85',
  },
];

export default function HomePage() {
  return (
    <div>
      <PageMeta
        title="Movement confidence, from a smartphone camera"
        description="Valanti Labs is developing AI-powered movement analysis using smartphone video — designed to help people better understand how they move through recovery, rehabilitation and everyday strength."
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="vl-container pt-12 lg:pt-20 pb-20 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <span
                data-testid={HERO.statusLabel}
                className="inline-flex items-center gap-2 mono-label text-vl-ink/70 bg-vl-ink/5 px-3 py-1.5 rounded-full"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-vl-violet vl-pulse" />
                App in pre-release preparation · Coming soon
              </span>
              <h1
                data-testid={HERO.headline}
                className="font-serif-display mt-6 text-[44px] sm:text-6xl lg:text-[76px] leading-[0.98] tracking-tight text-vl-ink"
              >
                Rebuilding<br />
                <em className="not-italic text-vl-violet">confidence</em><br />
                through movement.
              </h1>
              <p className="mt-7 text-lg text-vl-ink/70 max-w-xl leading-relaxed">
                Valanti Labs is developing AI-powered movement analysis that uses smartphone video to help people
                better understand how they move — through recovery, rehabilitation, adversity, everyday strength, and
                beyond.
              </p>
              <p className="mt-4 text-sm text-vl-ink/55 max-w-xl">
                No wearables. No specialised camera system. Structured movement feedback from video captured on a
                phone.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/early-access"
                  data-testid={HERO.primaryCta}
                  className="inline-flex items-center gap-2 rounded-full bg-vl-ink text-vl-bg px-6 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors"
                >
                  Join Early Access <ArrowRight size={16} />
                </Link>
                <Link
                  to="/how-it-works"
                  data-testid={HERO.secondaryCta}
                  className="inline-flex items-center gap-2 rounded-full border border-vl-ink/20 text-vl-ink px-6 py-3.5 text-sm font-medium hover:bg-vl-ink/5 transition-colors"
                >
                  See How It Works
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col items-center lg:items-end relative">
              <div className="absolute -inset-10 bg-gradient-to-tr from-vl-violet/10 via-transparent to-vl-medical/10 blur-3xl pointer-events-none" />
              <PhoneMockup className="relative" />
              <p className="relative mt-6 max-w-[280px] text-[11px] leading-snug text-vl-ink/55 text-center lg:text-right">
                Prototype interface shown for illustration. On-screen labels are unverified content; final release may differ.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Recovery does not end */}
      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="mono-label text-vl-violet mb-4">The gap</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-vl-ink">
              Recovery does not end when you leave the appointment.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 lg:border-l hairline space-y-5 text-[17px] text-vl-ink/75 leading-relaxed">
            <p>
              Many people receive movement or exercise guidance during a professional appointment, but once they
              return home the certainty can disappear.
            </p>
            <p>
              Instructions may be forgotten. Printed diagrams can be difficult to interpret. Pain, fatigue, medication,
              weakness, or fear can make even familiar movements feel uncertain.
            </p>
            <p>
              Valanti Labs is being built to help bridge the space between professional guidance and daily reality —
              not by replacing professional care, but by making structured movement feedback more accessible between
              appointments.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — How it works */}
      <section>
        <div className="vl-container py-24 lg:py-32">
          <SectionHeader
            kicker="How the app works"
            title="Three steps. Built for the phone already in your pocket."
            lead="A simple flow that respects how people actually move at home, in clinics, in gyms, in community spaces."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                n: '01',
                t: 'Choose and prepare',
                b: 'Choose a supported movement, review the recording guidance, and position the phone so the relevant body area is visible.',
              },
              {
                n: '02',
                t: 'Capture',
                b: 'Capture the movement using the workflow available in the release version.',
              },
              {
                n: '03',
                t: 'Review your feedback',
                b: 'Receive structured observations about visible movement patterns, supported performance dimensions, and areas that may deserve attention.',
              },
            ].map((s) => (
              <div
                key={s.n}
                className="rounded-3xl bg-vl-surface border hairline p-8 hover:shadow-md transition-shadow"
                data-testid={`step-${s.n}`}
              >
                <p className="font-mono text-sm text-vl-violet">{s.n}</p>
                <h3 className="font-serif-display text-2xl text-vl-ink mt-3">{s.t}</h3>
                <p className="text-sm text-vl-ink/65 mt-3 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — What the feedback helps notice */}
      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-24 lg:py-32">
          <SectionHeader
            kicker="What the feedback may help users notice"
            title="Structured observations — not certainty pretending to be science."
            lead="What the app surfaces depends on the supported movement, the video itself, and how reliable the available evidence is for each dimension."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {FEEDBACK_ITEMS.map(({ icon: Icon, label, body }) => (
              <div
                key={label}
                className="rounded-2xl bg-vl-surface border hairline p-5 hover:-translate-y-0.5 transition-transform"
              >
                <Icon size={18} className="text-vl-violet" />
                <h4 className="font-serif-display text-lg text-vl-ink mt-3 leading-tight">{label}</h4>
                <p className="text-[13px] text-vl-ink/65 mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4b — Structured feedback (Val Dashboard) */}
      <StructuredFeedbackSection />

      {/* SECTION 5 — Built for real life */}
      <section>
        <div className="vl-container py-24 lg:py-32">
          <SectionHeader
            kicker="Built for real life"
            title="Not perfect laboratory conditions."
            lead="Movement happens in bedrooms, living rooms, clinics, gyms, rehabilitation spaces and community environments. People use different phones. Bodies move differently. Valanti Labs has been researched around the reality that human movement and everyday video are imperfect."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {AUDIENCE_CARDS.map(({ icon: Icon, title, body, image }) => (
              <div
                key={title}
                className="rounded-3xl border hairline overflow-hidden bg-vl-surface group"
              >
                <div className="aspect-[16/9] overflow-hidden bg-vl-bg2">
                  <img
                    src={image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                  <Icon size={18} className="text-vl-violet" />
                  <h3 className="font-serif-display text-2xl text-vl-ink mt-3">{title}</h3>
                  <p className="text-sm text-vl-ink/70 mt-3 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — Technology that knows its limits */}
      <section className="bg-vl-ink text-vl-bg">
        <div className="vl-container py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="mono-label text-vl-bg/60 mb-4">Reliability awareness</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                Better feedback begins with knowing <em className="not-italic text-vl-violet/90">when not to guess.</em>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-vl-bg/75 leading-relaxed">
              <p>
                Many movement systems can produce a smooth or confident-looking answer even when the available
                evidence is weak.
              </p>
              <p>
                Valanti Labs has invested in research designed to improve how movement signals are interpreted and how
                uncertainty is handled.
              </p>
              <p>
                The goal is not to pretend that every video is perfect. The goal is to provide more meaningful
                movement feedback within clearly understood limits.
              </p>
              <Link
                to="/technology"
                className="inline-flex items-center gap-2 mt-4 text-vl-bg border-b border-vl-bg/30 pb-1 hover:border-vl-bg transition-colors"
              >
                Explore the technology <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="mt-16 [&_.rounded-2xl]:bg-vl-bg/5 [&_.rounded-2xl]:border-vl-bg/15 [&_.text-vl-ink]:text-vl-bg [&_.text-vl-ink\/65]:text-vl-bg/70 [&_.text-vl-ink\/45]:text-vl-bg/55">
            <Pipeline />
          </div>
        </div>
      </section>

      {/* SECTION 7 — Founder story teaser */}
      <section>
        <div className="vl-container py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border hairline aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1552650272-b8a34e21bc4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaG9tZSUyMHN0cmV0Y2hpbmd8ZW58MHx8fHwxNzgxNjAxNjI4fDA&ixlib=rb-4.1.0&q=85"
                alt="Calm home movement"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="mono-label text-vl-violet mb-4">The founder story</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-vl-ink">
              Born from adversity. Built for a broader human need.
            </h2>
            <p className="mt-6 text-base text-vl-ink/70 leading-relaxed max-w-2xl">
              Valanti Labs began after founder Chrissa Valanti Kathopoulis experienced how serious illness, treatment,
              pain, medication, spinal damage, and recovery can change a person's relationship with movement.
              Conversations with other people and health professionals revealed that the underlying problem was much
              larger.
            </p>
            <p className="mt-3 text-base text-vl-ink/70 leading-relaxed max-w-2xl">
              People facing many different forms of recovery can lose confidence in their own body.
            </p>
            <Link
              to="/why-valanti-labs"
              className="inline-flex items-center gap-2 mt-7 rounded-full border border-vl-ink/20 text-vl-ink px-5 py-3 text-sm font-medium hover:bg-vl-ink/5 transition-colors"
            >
              Read Why Valanti Labs Exists <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Research */}
      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="mono-label text-vl-violet mb-4">Research journey</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-vl-ink">
              Built by testing assumptions, not hiding uncertainty.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 lg:border-l hairline space-y-5 text-vl-ink/75 leading-relaxed">
            <p>
              The Valanti Labs research journey has included failed approaches, partial results, repeated testing,
              real-world video variation, and progressive refinement.
            </p>
            <p>
              That process matters because trustworthy movement technology must be developed around its limitations as
              well as its capabilities.
            </p>
            <Link
              to="/research"
              className="inline-flex items-center gap-2 mt-4 text-vl-ink border-b border-vl-ink/20 pb-1 hover:border-vl-ink transition-colors w-fit"
            >
              Explore the research journey <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Privacy */}
      <section>
        <div className="vl-container py-24 lg:py-32 text-center">
          <SectionHeader
            kicker="Privacy & control"
            title="Your movement data deserves clear boundaries."
            lead="Valanti Labs is committed to explaining, in plain language, what information is collected, why it is required, how it is handled, who may access it, and how users can request access, correction, or deletion."
            align="center"
          />
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 mt-10 rounded-full border border-vl-ink/20 text-vl-ink px-5 py-3 text-sm font-medium hover:bg-vl-ink/5 transition-colors"
          >
            Read our privacy approach <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* SECTION 10 — Early access CTA banner */}
      <section className="bg-vl-ink text-vl-bg">
        <div className="vl-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="mono-label text-vl-bg/55 mb-4">Early access</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Be part of the next stage.
            </h2>
            <p className="mt-5 text-base text-vl-bg/70 max-w-xl leading-relaxed">
              Join the early access list for product updates, testing opportunities, research news, and future launch
              information.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/early-access"
              className="inline-flex items-center gap-2 rounded-full bg-vl-bg text-vl-ink px-6 py-3.5 text-sm font-medium hover:bg-white transition-colors"
            >
              Register your interest <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
